<template>
  <div
    class="v-slider"
    :class="{'badge-onfocus': !!thumbLabel && thumbLabel !== 'always'}"
  >
    <div class="v-slider-input"
         :class="classes">
      <input
        :id="id"
        :min="min"
        :step="step"
        :max="max"
        :disabled="disabled"
        :style="styles"
        v-model="localValue"
        @mousedown="$emit('mousedown')"
        @mouseup="$emit('mouseup')"
        :class="[sliderVariant, {'dragging': isDragging}]"
        type="range"
        class="form-range"
      >

      <div class="v-badge-container"
           :style="computedBadge"
           :class="{'dragging': isDragging}"
           v-if="showThumbLabel">
        <div class="v-badge badge py-8"
             :class="thumbVariant">
          <span class="fw-normal text-xs">{{ label }}</span>
        </div>
      </div>
    </div>

    <label
      v-if="!!this.$slots.default"
      :for="id"
      class="form-label"
    >
      <span><slot/></span>
    </label>
  </div>
</template>

<script>
export default {
  name: 'VSlider',
  emits: ['mousedown', 'mouseup', 'dragging', 'start', 'end', 'change', 'input'],
  data() {
    return {
      localValue: this.value,
      mouseDown: false,
      mouseUp: false,
      isDragging: false,
    };
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    value: {},
    disabled: Boolean,
    color: {
      type: String,
      default: 'primary',
    },
    label: {
      type: [String, Number],
      default: '',
    },
    thumbLabel: {
      type: [Boolean, String],
      default: false,
    },
    thumbHeight: {
      type: Number,
      default: 16,
    },
    thumbColor: {
      type: String,
      default: 'primary',
    },
    step: {
      type: [String, Number],
      required: false,
      default: '1'
    },
    min: {
      type: [String, Number],
      required: false,
      default: '0'
    },
    max: {
      type: [String, Number],
      required: false,
      default: '100'
    },
  },
  computed: {
    styles() {
      return {
        'background-size': 'calc(' + (this.value - this.min) * 100 / (this.max - this.min) + '% '
          + (this.localValue < (this.max - this.min) / 2 ? '+ 6px' : ' - 6px') + ')' + ' 100%',
      }
    },
    computedBadge() {
      return {
        'transform': 'translateX(calc(' + (this.value - this.min) * 100 / (this.max - this.min) + '%))',
        '--var-bagde-h': this.thumbHeight + 'px' ?? '16px',
      }
    },
    showThumbLabel() {
      return !this.isDisabled && !!(
        this.thumbLabel || this.label
      )
    },
    sliderVariant() {
      return this.color ? `v-slider-${this.color}` : '';
    },
    thumbVariant() {
      return this.thumbColor ? `v-badge-${this.thumbColor}` : '';
    },
    classes() {
      return {
        'v-slider--start': this.min.toString() === this.value.toString(),
        'v-slider--end': this.max.toString() === this.value.toString(),
      }
    }
  },
  watch: {
    localValue(value, oldVal) {
      this.$emit('input', value)
      this.$emit('change', value)

      this.checkIfChanging(value, oldVal);

      if (value === this.min) {
        this.$emit('start', true)
      } else {
        this.$emit('start', false)
      }

      if (value === this.max) {
        this.$emit('end', true)
      } else {
        this.$emit('end', false)
      }
    },
    value(value) {
      this.localValue = value;
    },
  },
  methods: {
    checkIfChanging(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.isDragging = true;
        this.$emit('dragging', true);
      }

      setTimeout(() => {
        if (this.$data.localValue === newVal) {
          this.isDragging = false;
          this.$emit('dragging', false);
        }
      }, 150)
    }
  }
};
</script>

<style lang="scss">
@import "VSlider";
</style>
